/**
 * System instruction generator for "Run Selected Tasks" feature.
 * @see docs/issues/task-list/run-selected-tasks-instructions.md
 *
 * This generates the system instruction prepended to the parent task's description
 * when users select existing tasks to run as a task list.
 */

export type RunningMode = 'sequential' | 'ai-sequential' | 'ai-parallel';

export interface GenerateSystemInstructionParams {
  runningMode: RunningMode;
  subtaskIds: string[];
  userDescription: string;
}

/**
 * Generates the subtask IDs list for display.
 * Example output:
 * 0. Task ID: abc-123-def-456-... (full UUID - DO NOT shorten)
 * 1. Task ID: ghi-789-jkl-012-... (full UUID - DO NOT shorten)
 */
function formatSubtaskIdsList(subtaskIds: string[]): string {
  return subtaskIds.map((id, idx) => `${idx}. Task ID: ${id}`).join('\n');
}

/**
 * Generates the subtask IDs array for MCP tool call.
 * IMPORTANT: Uses FULL UUIDs, not shortened versions.
 * Example output: ["abc-123-def-456-...", "ghi-789-jkl-012-..."]
 */
function formatSubtaskIdsArray(subtaskIds: string[]): string {
  return `[${subtaskIds.map((id) => `"${id}"`).join(', ')}]`;
}

/**
 * Generates sequential dependencies array.
 * Creates a linear chain: task[0] -> task[1] -> task[2] -> ...
 */
function generateSequentialDependencies(subtaskIds: string[]): string {
  if (subtaskIds.length <= 1) return '[]';

  const deps = subtaskIds
    .slice(0, -1)
    .map((_, idx) => `{blocking_task_index: ${idx}, blocked_task_index: ${idx + 1}}`)
    .join(', ');

  return `[${deps}]`;
}

/**
 * Common workflow instructions shared across all running modes.
 */
const WORKFLOW_INSTRUCTIONS = `
## TASK LIST DOCUMENTATION FOLDER

All task lists MUST create a documentation folder to maintain context across sub-tasks. This enables:
- Sub-tasks to understand what previous sub-tasks did
- Parent-task to make reactive decisions before merging
- Future conflict resolution capabilities

**Folder Structure:**
\`\`\`
.agents/docs/
└── task-list-{task_list_id}/
    ├── overview.md           # Created by parent-task
    ├── progress.md           # Updated by parent-task after each merge
    ├── issues.md             # Running issue log (any agent can write)
    ├── decisions.md          # Cross-subtask architectural decisions
    └── subtasks/
        ├── subtask-1.md      # Created by sub-task 1 after completion
        ├── subtask-2.md      # Created by sub-task 2 after completion
        └── ...
\`\`\`

**⚠️ SECURITY WARNING:**
These documentation files are committed to git alongside code. NEVER write sensitive data such as:
- API keys, tokens, or secrets
- Passwords or credentials
- Private keys or certificates
- Environment variables containing secrets
- Internal URLs or IP addresses that should remain private

Only write information that is safe to be publicly visible in the repository.

## PRE-LOOP SETUP (CALL ONCE AND CACHE VALUES)
- Call \`get_context\` to get project_id (or \`list_projects\` if get_context unavailable)
- Call \`get_current_workspace_config\` to get repos and branch
- Call \`get_executor_config\` to get executor and variant for sub-tasks
- **CREATE DOCUMENTATION FOLDER**: Create \`.agents/docs/task-list-{task_list_id}/\` folder with initial files:
  - \`overview.md\`: Write the overall task description, goals, and constraints
  - \`progress.md\`: Initialize with "Task list created. Waiting for sub-tasks..."
  - \`issues.md\`: Create empty file for issue logging
  - \`decisions.md\`: Create empty file for decision logging
  - \`subtasks/\`: Create empty folder for sub-task summaries
- **COMMIT DOCUMENTATION**: After creating the documentation folder, commit the changes:
  - Stage all files in \`.agents/docs/task-list-{task_list_id}/\`
  - Commit with message: "docs: create task list documentation folder"
  - This ensures sub-tasks can see the documentation after rebasing
- Cache these values and reuse throughout the task list execution

## EXECUTION LOOP (CRITICAL - FOLLOW EXACTLY)
1. CALL \`get_subtasks_statuses\` PERIODICALLY:
   - Poll every 15 SECONDS (NOT every 1-2 seconds - this is token-wasteful)
   - IMPORTANT: Use the sleep tool or bash \`sleep 15\` command to wait 15 seconds between polls
   - The response contains: ready_tasks, in_review_tasks, pending_tasks, completed_tasks, in_progress_tasks

2. MERGE IN-REVIEW TASKS (BEFORE STARTING NEW TASKS):
   - **MERGE FIRST**: Call \`merge_to_parent(workspace_id)\` for EACH in_review task
     - Use the cached workspace_id from the corresponding \`start_workspace_session\` response
     - Tasks in completed_tasks (status: Done) are already merged - skip them
   - **READ DOCUMENTATION AFTER MERGE**: Read all files in \`.agents/docs/task-list-{task_list_id}/\` folder:
     - Check \`issues.md\` for unresolved blocking issues
     - Read \`subtasks/{subtask-id}.md\` for the specific sub-task's STATUS field
     - Check \`decisions.md\` for any decisions that affect subsequent work
   - **REACTIVE DECISION MAKING**:
     - If STATUS=failed or SEVERITY=blocking in issues.md:
       * The sub-task committed ONLY docs (not code changes) to signal the issue
       * Mark the task as incomplete (do NOT proceed with next sub-tasks that depend on it)
       * STOP the session and notify user about the blocking issue
       * When user continues the session, restart this sub-task using \`start_workspace_session\`
     - If STATUS=partial: Decide whether to continue or notify user based on the issue severity
     - If STATUS=success: Proceed to update progress
   - **UPDATE PROGRESS**: After successful merge, update \`progress.md\` with:
     - Which sub-task was merged
     - Timestamp of merge
     - Current state of the task list
   - **COMMIT ALL CHANGES**: Commit all updated files:
     - Stage all changes (including \`.agents/docs/task-list-{task_list_id}/\` and any other modified files)
     - Commit with message: "docs: update task list progress after merging sub-task"
     - This ensures subsequent sub-tasks see the latest state after rebasing
   - DO NOT start any new tasks until ALL InReview tasks are processed

3. START READY TASKS (USE CACHED REPOS, BRANCH, AND EXECUTOR CONFIG):
   - Call \`start_workspace_session\` for EACH task in ready_tasks
   - Pass the CACHED repo_ids from setup (DO NOT pass project_id as repo_id)
   - CRITICAL: Use the TOP-LEVEL \`branch\` field from \`get_current_workspace_config\` as \`base_branch\` for ALL repos
     - Example: If branch="task-123" and repos=[{repo_id: "...", target_branch: "main"}], pass base_branch="task-123" (NOT "main")
     - This ensures sub-tasks merge back to parent's working branch, not the original base branch
   - Pass the CACHED executor and variant from \`get_executor_config\` (ensures sub-tasks use same executor profile as parent)
   - CACHE the returned workspace_id for each sub-task (you'll need it for \`merge_to_parent\`)
   - Each sub-task runs in its own isolated workspace session
   - DO NOT work on sub-tasks in the current/parent session

4. REPEAT THE LOOP:
   - Use \`sleep 15\` to wait before polling again
   - Continue polling \`get_subtasks_statuses\` every 15 seconds
   - Always merge InReview tasks first (step 2), then start tasks using CACHED values (step 3)
   - Do NOT stop the parent task session until ALL sub-tasks are completed (status: "done" or "cancelled")

## SESSION PERSISTENCE
- NEVER stop the parent session until ALL tasks in the list are done (status: "done" or "cancelled")
- The session must remain active throughout the entire task list execution`;

/**
 * Generates system instruction for SEQUENTIAL mode.
 * Tasks execute in order: task[0] -> task[1] -> task[2] -> ...
 */
function generateSequentialInstruction(subtaskIds: string[], userDescription: string): string {
  const subtaskIdsList = formatSubtaskIdsList(subtaskIds);
  const subtaskIdsArray = formatSubtaskIdsArray(subtaskIds);
  const dependencies = generateSequentialDependencies(subtaskIds);

  return `--- TASK LIST SYSTEM INSTRUCTION ---
This is a task list parent task. You are coordinating the execution of EXISTING sub-tasks.

## IMPORTANT: EXISTING TASKS
The sub-tasks below ALREADY EXIST. Do NOT create new tasks. Instead, LINK these existing tasks using the \`link_tasks_to_task_list\` MCP tool.

## RUNNING MODE: SEQUENTIAL

Execute tasks in order. Each task must complete before the next starts.
Dependencies: task[0] -> task[1] -> task[2] -> ...

## SUB-TASKS TO LINK
${subtaskIdsList}

(Note: Use the FULL UUID for each task ID - do not shorten them.)

## YOUR FIRST ACTION
Call the \`link_tasks_to_task_list\` MCP tool with:
- parent_task_id: The ID of this task (from \`get_context\`, or derive from \`get_current_workspace_config\`)
- subtask_ids: ${subtaskIdsArray}
- dependencies: ${dependencies}

This creates a linear chain where task[0] has no blockers, task[1] waits for task[0], etc.

After linking, follow the execution loop below.
${WORKFLOW_INSTRUCTIONS}

--- USER DESCRIPTION ---
${userDescription || '(No additional description provided)'}`;
}

/**
 * Generates system instruction for AI-SEQUENTIAL mode.
 * AI analyzes tasks and determines optimal sequential order.
 */
function generateAiSequentialInstruction(subtaskIds: string[], userDescription: string): string {
  const subtaskIdsList = formatSubtaskIdsList(subtaskIds);
  const subtaskIdsArray = formatSubtaskIdsArray(subtaskIds);

  return `--- TASK LIST SYSTEM INSTRUCTION ---
This is a task list parent task. You are coordinating the execution of EXISTING sub-tasks.

## IMPORTANT: EXISTING TASKS
The sub-tasks below ALREADY EXIST. Do NOT create new tasks. Instead, LINK these existing tasks using the \`link_tasks_to_task_list\` MCP tool.

## RUNNING MODE: AI-SEQUENTIAL

Analyze task dependencies and execute in the optimal order. Use your judgment to determine which tasks block others.
All tasks must execute SEQUENTIALLY (one at a time) to avoid git conflicts.

## SUB-TASKS TO LINK
${subtaskIdsList}

(Note: Use the FULL UUID for each task ID - do not shorten them.)

## YOUR FIRST ACTIONS
1. ANALYZE the sub-tasks to understand their dependencies and relationships
   - Read each sub-task's description using \`get_task\` if needed
   - Determine which tasks should run first based on logical dependencies
2. Call the \`link_tasks_to_task_list\` MCP tool with:
   - parent_task_id: The ID of this task (from \`get_context\`, or derive from \`get_current_workspace_config\`)
   - subtask_ids: ${subtaskIdsArray}
   - dependencies: Create sequential dependencies based on your analysis
     - Create a linear chain: [{blocking_task_index: first, blocked_task_index: second}, ...]
     - Even if tasks are logically independent, chain them sequentially to prevent git conflicts

After linking, follow the execution loop below.
${WORKFLOW_INSTRUCTIONS}

--- USER DESCRIPTION ---
${userDescription || '(No additional description provided)'}`;
}

/**
 * Generates system instruction for AI-PARALLEL mode.
 * AI analyzes tasks and determines which can run in parallel.
 */
function generateAiParallelInstruction(subtaskIds: string[], userDescription: string): string {
  const subtaskIdsList = formatSubtaskIdsList(subtaskIds);
  const subtaskIdsArray = formatSubtaskIdsArray(subtaskIds);

  return `--- TASK LIST SYSTEM INSTRUCTION ---
This is a task list parent task. You are coordinating the execution of EXISTING sub-tasks.

## IMPORTANT: EXISTING TASKS
The sub-tasks below ALREADY EXIST. Do NOT create new tasks. Instead, LINK these existing tasks using the \`link_tasks_to_task_list\` MCP tool.

## RUNNING MODE: AI-PARALLEL

Execute tasks in parallel where possible. Only tasks that depend on each other need to wait.
Analyze tasks yourself to determine dependencies and parallelization opportunities.

**WARNING:** Tasks running in parallel may cause git conflicts if they modify the same files. Only run tasks in parallel if they work on different files/components.

## SUB-TASKS TO LINK
${subtaskIdsList}

(Note: Use the FULL UUID for each task ID - do not shorten them.)

## YOUR FIRST ACTIONS
1. ANALYZE the sub-tasks to understand their dependencies and relationships
   - Read each sub-task's description using \`get_task\` if needed
   - Determine which tasks can run in parallel (no dependencies between them, different files)
   - Identify tasks that must wait for others to complete
2. Call the \`link_tasks_to_task_list\` MCP tool with:
   - parent_task_id: The ID of this task (from \`get_context\`, or derive from \`get_current_workspace_config\`)
   - subtask_ids: ${subtaskIdsArray}
   - dependencies: Create dependencies based on your analysis:
     - Independent tasks can have empty dependencies (pass empty array)
     - Only add dependencies where there are actual logical dependencies OR risk of git conflicts
     - Example: If tasks 0 and 1 can run in parallel, but task 2 depends on both:
       [{blocking_task_index: 0, blocked_task_index: 2}, {blocking_task_index: 1, blocked_task_index: 2}]

After linking, follow the execution loop below.
${WORKFLOW_INSTRUCTIONS}

--- USER DESCRIPTION ---
${userDescription || '(No additional description provided)'}`;
}

/**
 * Generates the complete system instruction for the parent task.
 *
 * @param params - Parameters including running mode, subtask IDs, and user description
 * @returns The complete system instruction string to prepend to the parent task description
 */
export function generateSystemInstruction(params: GenerateSystemInstructionParams): string {
  const { runningMode, subtaskIds, userDescription } = params;

  switch (runningMode) {
    case 'sequential':
      return generateSequentialInstruction(subtaskIds, userDescription);
    case 'ai-sequential':
      return generateAiSequentialInstruction(subtaskIds, userDescription);
    case 'ai-parallel':
      return generateAiParallelInstruction(subtaskIds, userDescription);
    default:
      throw new Error(`Unknown running mode: ${runningMode}`);
  }
}